import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { judgeRange, missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Digital noise check.
 *
 * Noise matters for document photos because consular systems re-compress and
 * sometimes re-print the image: heavy noise on top of JPEG compression is what
 * makes a technically valid photo look "dirty" and unreadable.
 */
export const noiseCheck: ValidationCheck = {
  id: 'noise',
  title: 'Шум',
  description: 'Оценивает уровень шума матрицы.',
  kind: 'quality',
  requires: ['quality'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['quality']);
    if (missing) return notChecked(ctx, noiseCheck, missing, ctx.rule.severity);
    const quality = ctx.quality;
    if (!quality) return notChecked(ctx, noiseCheck, 'Метрики качества недоступны.', ctx.rule.severity);

    const maxSigma = numberParam(ctx.rule.params, 'maxSigma', 7);
    const { sigma, medianDeviation } = quality.noise;

    const verdict = judgeRange({ value: sigma, max: maxSigma, warningMargin: 0.2 });

    return plainResult({
      id: noiseCheck.id,
      title: noiseCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      value: roundTo(sigma, 2),
      max: maxSigma,
      message:
        verdict.status === 'pass'
          ? 'Уровень шума в норме.'
          : verdict.status === 'warning'
            ? 'Заметный уровень шума — изображение выглядит зернистым.'
            : 'Высокий уровень шума: изображение зернистое, детали лица хуже различимы.',
      requirement: 'Фотография должна быть чистой, без выраженного зерна и цветного шума.',
      fixability: 'retake',
      confidence: 0.65,
      details: {
        sigma: roundTo(sigma, 2),
        medianDeviation: roundTo(medianDeviation, 4),
        maxSigma,
        sampledAt: quality.sampledAt,
        displayValue: roundTo(sigma, 1).toFixed(1).replace('.', ','),
      },
    });
  },
};
